import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import Button from '../../../../reusable/Button/Button';
import Input from '../../../../reusable/Input/Input';
import NumberInput from '../../../../reusable/NumberInput/NumberInput';
import Select from '../../../../reusable/Select/Select';
import TextArea from '../../../../reusable/TextArea/TextArea';
import { toast } from '../../../../reusable/Toast/toastStore';
import { selectUser } from '../../../../store/slices/authSlice';
import { useGetProfileDetailsQuery } from '../../../../store/api/profileApi';
import { useSubmitSuperAffiliateApplicationMutation } from '../../../../store/api/affiliateApi';
import { DEFAULT_DIAL_CODE, findCountry } from '../../../../utilities/country';
import { apiMessage } from './promoCodes';
import { t } from '../../../../i18n/i18n';

const PLATFORMS = ['TikTok', 'Instagram', 'X', 'YouTube', 'Kick', 'Facebook'].map((p) => ({ value: p, label: p }));

/** One field per network, each its own payload key — live's form. */
const SOCIAL_LINKS = [
  { key: 'tiktok_url', label: 'Please submit a link to your TikTok' },
  { key: 'instagram_url', label: 'Please submit a link to your Instagram' },
  { key: 'x_url', label: 'Please submit a link to your X' },
  { key: 'youtube_url', label: 'Please submit a link to your YouTube' },
  { key: 'kick_url', label: 'Please submit a link to your Kick' },
  { key: 'facebook_url', label: 'Please submit a link to your Facebook' },
];

const REQUIRED = {
  full_name: 'Full Name',
  email: 'Email',
  country: 'Where are you from?',
  content_description: 'How would you describe your content?',
  primary_platform: 'Which is the most popular platform you post on?',
  audience_location: 'Where is your audience based?',
};

const EMPTY = {
  full_name: '',
  email: '',
  dial_code: DEFAULT_DIAL_CODE,
  phone_number: '',
  country: '',
  content_description: '',
  // null, not undefined: the shared Select reads undefined as "no value given"
  // and shows its country default.
  primary_platform: null,
  audience_location: '',
  follower_count: '',
  ...Object.fromEntries(SOCIAL_LINKS.map((s) => [s.key, ''])),
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Super Affiliate Application — live's form (`ApplicationModal.jsx` on main),
 * same fields, same checks, same payload. What the profile already knows is
 * filled in, so the applicant doesn't type it twice.
 */
export default function ApplicationDialog({ open, onClose, onSubmitted }) {
  const user = useSelector(selectUser);
  const { data: profile } = useGetProfileDetailsQuery(undefined, { skip: !open });
  const [submit, { isLoading: submitting }] = useSubmitSuperAffiliateApplicationMutation();

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  const prefill = useMemo(() => {
    const dial = String(profile?.country_code ?? '').replace(/\D/g, '');
    return {
      full_name: [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim(),
      email: profile?.email || user?.email || '',
      dial_code: dial ? `+${dial}` : '',
      phone_number: String(profile?.contact_number ?? '').replace(/\D/g, ''),
      country: findCountry(profile?.country)?.name ?? profile?.country ?? '',
    };
  }, [profile, user]);

  // Blanks only, so a profile that lands after the dialog opened never
  // overwrites what the applicant has typed.
  useEffect(() => {
    if (!open) return;
    setForm((prev) => {
      const next = { ...prev };
      Object.entries(prefill).forEach(([key, value]) => {
        if (value && (!next[key] || (key === 'dial_code' && !prev.phone_number))) next[key] = value;
      });
      return next;
    });
  }, [open, prefill]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };
  const field = (key) => (event) => set(key, event.target.value);

  const validate = () => {
    const next = {};
    Object.entries(REQUIRED).forEach(([key, label]) => {
      if (!String(form[key] ?? '').trim()) next[key] = t('{{field}} is required', { field: t(label) });
    });
    if (form.email && !EMAIL.test(form.email.trim())) next.email = t('Enter a valid email address');
    const digits = String(form.phone_number).replace(/\D/g, '');
    if (!form.dial_code) next.phone_number = t('Select a country code');
    else if (!digits) next.phone_number = t('WhatsApp Number is required');
    else if (digits.length < 6) next.phone_number = t('Enter a valid number');
    if (form.follower_count && Number.isNaN(Number(form.follower_count))) next.follower_count = t('Enter a number');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const close = () => {
    if (submitting) return;
    setErrors({});
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !validate()) return;

    // Blank optional fields are left off rather than sent as "".
    const { dial_code: dialCode, ...rest } = form;
    const body = Object.entries(rest).reduce((acc, [key, value]) => {
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed !== '' && trimmed != null) acc[key] = trimmed;
      return acc;
    }, {});
    // The API stores one dialled number, "+447700900123".
    body.phone_number = `+${dialCode.replace(/\D/g, '')}${String(form.phone_number).replace(/\D/g, '')}`;

    try {
      const response = await submit(body).unwrap();
      toast.success(t('Application submitted'), t('We will review it shortly.'));
      setForm(EMPTY);
      onSubmitted?.(response);
      onClose();
    } catch (err) {
      const data = err?.data;
      const detail = data?.detail || data?.message || data?.error;
      // Field errors come back keyed by field name.
      if (data && typeof data === 'object' && !detail) {
        const mapped = {};
        Object.entries(data).forEach(([key, value]) => {
          if (key in EMPTY) mapped[key] = Array.isArray(value) ? value[0] : String(value);
        });
        if (Object.keys(mapped).length) setErrors(mapped);
      }
      toast.error(t('Could not submit the application'), apiMessage(err, t('Please try again.')));
    }
  };

  const text = (key, label, props = {}) => (
    <Input
      variant="form"
      label={t(label)}
      required={key in REQUIRED}
      value={form[key]}
      onChange={field(key)}
      error={errors[key]}
      {...props}
    />
  );

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      centered
      width={760}
      destroyOnHidden
      className="aff-dialog"
      rootClassName="aff-dialog-root"
      aria-label={t('Super Affiliate application')}
    >
      <form className="sa-apply" onSubmit={handleSubmit} noValidate>
        <h2 className="aff-dialog__title">{t('Super Affiliate Application')}</h2>

        <div className="aff-dialog__scroll">
          <div className="sa-apply__grid">
            {text('full_name', 'Full Name', { autoComplete: 'name' })}
            {text('email', 'Email', { type: 'email', autoComplete: 'email' })}
            <div className="sa-apply__phone">
              <NumberInput
                label={
                  <>
                    {t('WhatsApp Number')}<span className="reusable-input-required">*</span>
                  </>
                }
                value={form.phone_number}
                onChange={field('phone_number')}
                countryCode={form.dial_code}
                onCountryCodeChange={(code) => set('dial_code', code)}
                error={errors.phone_number}
                placeholder="7700900123"
                inputMode="numeric"
                autoComplete="tel-national"
              />
            </div>
            {text('country', 'Where are you from?')}

            <div className="sa-apply__wide">
              <TextArea
                variant="form"
                label={t('How would you describe your content?')}
                required
                rows={3}
                value={form.content_description}
                onChange={field('content_description')}
                error={errors.content_description}
              />
            </div>

            <Select
              variant="form"
              label={t('Which is the most popular platform you post on?')}
              required
              placeholder={t('Select a platform')}
              options={PLATFORMS}
              value={form.primary_platform}
              onChange={field('primary_platform')}
              error={errors.primary_platform}
            />
            {text('audience_location', 'Where is your audience based?')}
            {text('follower_count', 'How many followers do you have on your most popular platform?', {
              inputMode: 'numeric',
            })}
          </div>

          <div className="sa-apply__section">
            <h3 className="sa-apply__section-title">{t('Social Media Links')}</h3>
            <p className="aff-dialog__intro">{t('Please also include the profiles you post on.')}</p>
          </div>

          <div className="sa-apply__grid">
            {SOCIAL_LINKS.map(({ key, label }) => (
              <div key={key}>{text(key, label, { type: 'url', placeholder: 'https://' })}</div>
            ))}
          </div>
        </div>

        <div className="aff-dialog__actions">
          <Button variant="ghost" height={38} onClick={close} disabled={submitting}>
            {t('Cancel')}
          </Button>
          <Button variant="submit" height={38} htmlType="submit" loading={submitting}>
            {t('Submit Application')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
